import { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { kebabCase } from "lodash";

import { Button, Form, FormControl } from "@hydra/atom/components";

import { TableWithCheckbox } from "@/components/finance/account-receivables";
import {
  getPurchaseOrderItemDetailTableColumns,
  getMaterialRequestItemDetailTableColumns,
} from "@/components/finance/account-receivables/tableWithCheckboxData";
import { LookupField } from "@/components/dynamic/fields";
import { BoxedContent, Header, FormLeftHeader } from "@/components/common";
import {
  getDynamicObjectRecordById,
  updateDynamicObjectRecord,
} from "@/api/dynamic/dynamicObjectNameApi";
import dynamicObjectMap from "@/utils/maps/dynamicObjectMap";
import showToast from "@/utils/toast/helpers";

const validationMessages = {
  required: {
    required: "This field is required",
  },
};

const formatPurchaseOrder = (purchaseOrder) => {
  const {
    id, number, supplier, itemDetail
  } = purchaseOrder;

  const data = {
    itemDetail: itemDetail
      .filter((i) => !i.materialRequestItemDetailId)
      .map((item) => ({
        ...item,
        purchaseOrder: {
          label: number,
          value: id,
        },
        item: {
          label: item.item.itemName,
          value: item.item.id,
        },
        supplier: {
          label: supplier.name,
          value: supplier.id,
        },
        unitOfMeasure: item.unitOfMeasure ?
          {
            label: item.unitOfMeasure.name,
            value: item.unitOfMeasure.id,
          } :
          null,
      })),
  };

  return data;
};

const formatMaterialRequest = (materialRequest) => {
  const { id, number, itemDetail } = materialRequest;

  const data = {
    itemDetail: itemDetail
      .filter((i) => ["Pending", "Partial"].includes(i.converted))
      .map((item) => ({
        ...item,
        quantity: Number(item.quantity) - Number(item.convertedQuantity || 0),
        rate: item.amount,
        materialRequest: {
          label: number,
          value: id,
        },
        item: {
          label: item.item.itemName,
          value: item.item.id,
        },
        supplier: {
          label: item.supplier.name,
          value: item.supplier.id,
        },
        unitOfMeasure: item.unitOfMeasure ?
          {
            label: item.unitOfMeasure.name,
            value: item.unitOfMeasure.id,
          } :
          null,
      })),
  };

  return data;
};

export default function PurchaseOrderMaterialRequestForm() {
  const [state, setState] = useState({
    purchaseOrder: null,
    materialRequest: null,
    purchaseOrderItemDetail: [],
    materialRequestItemDetail: [],
  });

  const submitButtonRef = useRef(null);
  const navigate = useNavigate();

  const handleChange = (key, value) => {
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const { data: purchaseOrderData } = useQuery(
    [
      kebabCase(dynamicObjectMap.get("PurchaseOrderObjectName")),
      state?.purchaseOrder?.value,
    ],
    () =>
      getDynamicObjectRecordById(
        dynamicObjectMap.get("PurchaseOrderObjectName"),
        state.purchaseOrder?.value
      ),
    {
      enabled: Boolean(state.purchaseOrder),
    }
  );

  useEffect(() => {
    if (state?.purchaseOrder && purchaseOrderData) {
      const formattedPurchaseOrder = formatPurchaseOrder(purchaseOrderData);
      if (!formattedPurchaseOrder.itemDetail.length) {
        showToast("No purchase order line items pending to be linked", "info");
        return;
      }
      handleChange(
        "purchaseOrderItemDetail",
        formattedPurchaseOrder.itemDetail
      );
    }
  }, [state?.purchaseOrder, purchaseOrderData]);

  const { data: materialRequestData } = useQuery(
    [
      kebabCase(dynamicObjectMap.get("MaterialRequestObjectName")),
      state?.materialRequest?.value,
    ],
    () =>
      getDynamicObjectRecordById(
        dynamicObjectMap.get("MaterialRequestObjectName"),
        state.materialRequest?.value
      ),
    {
      enabled: Boolean(state.materialRequest),
    }
  );

  useEffect(() => {
    if (state?.materialRequest && materialRequestData) {
      const formattedMaterialRequest =
        formatMaterialRequest(materialRequestData);
      if (!formattedMaterialRequest.itemDetail.length) {
        showToast(
          "No material request line items pending to be linked",
          "info"
        );
        return;
      }
      handleChange(
        "materialRequestItemDetail",
        formattedMaterialRequest.itemDetail
      );
    }
  }, [state?.materialRequest, materialRequestData]);

  const updateMutation = useMutation(
    ({ id: recordId, data: recordData, objectName }) =>
      updateDynamicObjectRecord(objectName, recordId, recordData),
    {
      onError: () => {
        showToast("Could not update. Try again!", "error");
      },
      onSuccess: (response, variables) => {
        const { id, data } = variables;
        const purchaseOrderItemDetailId = id;
        const { materialRequestItemDetailId } = data;
        const { purchaseOrderItemDetail, materialRequestItemDetail } = state;
        handleChange(
          "purchaseOrderItemDetail",
          purchaseOrderItemDetail.filter(
            (p) => p.id !== purchaseOrderItemDetailId
          )
        );
        handleChange(
          "materialRequestItemDetail",
          materialRequestItemDetail.filter(
            (m) => m.id !== materialRequestItemDetailId
          )
        );

        showToast("Linked successfully", "success");
      },
    }
  );

  const handleCancel = () => {
    navigate(-1);
  };

  const handleSave = () => {
    submitButtonRef.current.click();
  };

  const handleSubmit = async () => {
    const selectedPurchaseOrderItemDetail = state.purchaseOrderItemDetail.find(
      (q) => q.isSelected
    );

    if (!selectedPurchaseOrderItemDetail) {
      showToast("No purchase order line item is selected", "error");
      return;
    }

    const selectedMaterialRequestItemDetail =
      state.materialRequestItemDetail.find((q) => q.isSelected);

    if (!selectedMaterialRequestItemDetail) {
      showToast("No material request line item is selected", "error");
      return;
    }

    if (
      selectedPurchaseOrderItemDetail.item.value !==
      selectedMaterialRequestItemDetail.item.value
    ) {
      showToast("Item in both selected line items is different", "error");
      return;
    }

    if (
      selectedPurchaseOrderItemDetail.quantity >
      selectedMaterialRequestItemDetail.quantity
    ) {
      showToast(
        "Purchase order line item quantity cannot be greater than the material request line item quantity",
        "error"
      );
      return;
    }

    updateMutation.mutate({
      objectName: dynamicObjectMap.get("PurchaseOrderItemDetailObjectName"),
      id: selectedPurchaseOrderItemDetail.id,
      data: {
        materialRequestItemDetailId: selectedMaterialRequestItemDetail.id,
        materialRequest: selectedMaterialRequestItemDetail.materialRequest.value
      },
    });
  };

  return (
    <BoxedContent className="gl-account-form">
      <Header
        showBreadcrumb
        leftContent={<h1>Link Purchase Order to Material Request</h1>}
      />
      <Form
        onSubmit={handleSubmit}
        key="currency-form"
        className="dynamic-object-form"
        shouldScrollOnError
      >
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Select PO and MR"
              subtitle="Link Purchase Order with Material Request"
              icon="scan-icon"
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-6">
                <FormControl>
                  <LookupField
                    field={{
                      fieldName: "purchaseOrder",
                      name: "purchaseOrder",
                      label: "Purchase Order",
                      lookupObjectName: dynamicObjectMap.get(
                        "PurchaseOrderObjectName"
                      ),
                      lookupDisplayFieldName: ["number"],
                      lookupFieldFilter: [],
                      required: true,
                    }}
                    value={state.purchaseOrder}
                    onChange={(value) => handleChange("purchaseOrder", value)}
                    placeholder="Select Purchase Order"
                    apiFilters={{
                      "status[in]": "Approved,Partial,Draft,InProgress",
                    }}
                    rules="required"
                    messages={validationMessages.required}
                  />
                </FormControl>
                <TableWithCheckbox
                  data={state.purchaseOrderItemDetail}
                  columns={getPurchaseOrderItemDetailTableColumns()}
                  setData={(value) =>
                    handleChange("purchaseOrderItemDetail", value)}
                />
              </div>
              <div className="col-md-6">
                <FormControl>
                  <LookupField
                    field={{
                      name: "materialRequest",
                      label: "Material Request",
                      lookupObjectName: dynamicObjectMap.get(
                        "MaterialRequestObjectName"
                      ),
                      lookupDisplayFieldName: ["number"],
                      lookupFieldFilter: [],
                      required: true,
                    }}
                    value={state.materialRequest}
                    onChange={(value) => handleChange("materialRequest", value)}
                    placeholder="Select Material Request"
                    apiFilters={{
                      "status[in]": "Approved,Partial,Draft,InProgress",
                    }}
                    rules="required"
                    messages={validationMessages.required}
                  />
                </FormControl>
                <TableWithCheckbox
                  data={state.materialRequestItemDetail}
                  columns={getMaterialRequestItemDetailTableColumns()}
                  setData={(value) =>
                    handleChange("materialRequestItemDetail", value)}
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            style={{ display: "none" }}
            ref={submitButtonRef}
          >
            Link
          </button>
        </div>
      </Form>
      <div className="col-md-12">
        <div className="hr" />
      </div>

      <div className="col">
        <div className="buttons-at-end">
          <Button
            bordered
            small
            onClick={handleCancel}
            testId="Form-Cancel-Button"
          >
            Cancel
          </Button>
          <Button
            small
            className="save-btn"
            loading={updateMutation.isLoading}
            onClick={handleSave}
            testId="Form-Submit-Button"
          >
            Link
          </Button>
        </div>
      </div>
    </BoxedContent>
  );
}
